/**
 * Assistant Platform v1 shared contracts.
 *
 * Frozen at execution-plan Step 0.2. Every lane (backend workflow engine,
 * python assistant brain, frontend run UI) builds against this file, so a
 * change here is a cross-lane change and needs the orchestrator.
 *
 * Naming. Everything is prefixed `Assistant*` or `Workflow*`, with the small
 * set of exceptions the contract names outright (`Skill`, `SkillSummary`,
 * `DocumentCitation`, `WebCitation`). `apps/workflow-console/` is an unrelated
 * internal Argo console carrying its own step model: a collision between the
 * two would be a real bug, not a naming nit.
 *
 * Casing. Markdown templates and Postgres columns are snake_case; the wire and
 * this file are camelCase. The template compiler owns that translation, and the
 * per-field mapping is written out beside each type below.
 *
 * Row ids. Per CLAUDE.md nothing here exposes a numeric DB row id. Where the
 * DDL carries one, this file carries the `token`, `slug`, or display name that
 * stands in for it, and the mapping is stated so the schema author can check it.
 */

import type { ThreadArtifactDto } from "./thread-artifacts";
import type {
  SocialCitationPost,
  SocialTrackerProposal,
} from "./social-listening";

/* ------------------------------------------------------------------------ */
/* JSON primitives                                                           */
/* ------------------------------------------------------------------------ */

export type WorkflowJsonValue =
  | string
  | number
  | boolean
  | null
  | Array<WorkflowJsonValue>
  | { [key: string]: WorkflowJsonValue };

export interface WorkflowJsonObject {
  [key: string]: WorkflowJsonValue;
}

/* ------------------------------------------------------------------------ */
/* Template: inputs                                                          */
/* ------------------------------------------------------------------------ */

/** Frontmatter `inputs[].type`. Closed set: an unknown type is a compile error. */
export type WorkflowInputType = "text" | "audience" | "select" | "stimulus";

export interface WorkflowInputOption {
  value: string;
  label: string;
}

/**
 * One declared run input. Compiled from a frontmatter `inputs:` entry.
 *
 * md -> ts: `key`, `label`, `type`, `required`, `options` (options is required
 * when `type` is `select` and rejected otherwise), `placeholder` (allowed only
 * when `type` is `text`).
 *
 * When `type` is `stimulus`, the run value is a JSON-serialized
 * `Array<AssetMediaRef>` string. The server rebuilds URL fields from
 * org-scoped `files` rows and never trusts
 * client-supplied `publicUrl` / `gcsUri`.
 */
export interface WorkflowInputSpec {
  /** snake_case, unique within the template. Referenced as `input.<key>`. */
  key: string;
  label: string;
  type: WorkflowInputType;
  required: boolean;
  options?: Array<WorkflowInputOption>;
  /**
   * Example ghost text for a `text` input, authored in the template
   * frontmatter. Display-only: it teaches by example and is never submitted
   * as a default value.
   */
  placeholder?: string;
}

/** Values a run was started with, keyed by `WorkflowInputSpec.key`. */
export type WorkflowInputValue = string | null;

export interface WorkflowInputValues {
  [key: string]: WorkflowInputValue;
}

/* ------------------------------------------------------------------------ */
/* Template: steps                                                           */
/* ------------------------------------------------------------------------ */

/**
 * Closed step vocabulary. An unknown kind is a hard compile error, never a
 * skipped step and never a prose fallback.
 *
 * `workflow_run_steps.step_kind` is varchar rather than a pg enum, so this
 * array is the only enforcement the vocabulary has. Widening it is a contract
 * change, not a migration.
 */
export const WORKFLOW_STEP_KINDS = [
  "prompt",
  "gate",
  "quick_study",
  "slack_webhook",
  "report",
] as const;

export type WorkflowStepKind = (typeof WORKFLOW_STEP_KINDS)[number];

export function isWorkflowStepKind(value: string): value is WorkflowStepKind {
  return (WORKFLOW_STEP_KINDS as ReadonlyArray<string>).includes(value);
}

/** Kinds whose `instructions` field may be replaced by an org overlay. */
export function isOverridableStepKind(kind: WorkflowStepKind): boolean {
  return kind === "prompt" || kind === "report";
}

/** Kinds that spend money or reach outside the platform. Gate them. */
export const WORKFLOW_SPENDING_STEP_KINDS: ReadonlyArray<WorkflowStepKind> = [
  "quick_study",
  "slack_webhook",
];

/**
 * Kinds that leave an artifact behind for a later step to read.
 *
 * A reference (`inputs_from`, `questions_from`, `content_from`,
 * `sections_from`, `artifacts`) may only name a step of one of these kinds. A
 * `gate` records a decision and a `slack_webhook` records a delivery receipt;
 * neither produces content, so naming one is always a mistake rather than an
 * empty input at run time.
 */
export const WORKFLOW_ARTIFACT_PRODUCING_STEP_KINDS: ReadonlyArray<WorkflowStepKind> =
  ["prompt", "quick_study", "report"];

/**
 * Ceiling for `prompt.count`, the concurrent-draft fan-out width.
 *
 * Lives here rather than in the compiler because the composer validates a
 * workflow's shape client-side too, and a cap held in one backend constant plus
 * a line of prose drifts the moment somebody changes one of them.
 */
export const WORKFLOW_PROMPT_FANOUT_CAP = 12;

/** Fields every kind carries. `title` comes from the H2 heading, not the fence. */
export interface WorkflowStepConfigCommon {
  /** snake_case, unique within the template. `key:` in the step fence. */
  key: string;
  /** The H2 heading text above the fence. Display only. */
  title: string;
}

/**
 * An LLM step. The prose after the fence is its instruction body, and prose is
 * the only thing that body may be: anything with a cost or an external side
 * effect is declared in the fence, never inferred from the instructions.
 *
 * md -> ts: `routing_key` -> `routingKey`, `output` -> `output`,
 * `count` -> `count`, `skill` -> `skill`, `inputs_from` -> `inputsFrom`.
 */
export interface WorkflowPromptStepConfig extends WorkflowStepConfigCommon {
  kind: "prompt";
  /** Must resolve in `config/llm-routing.yaml`. No model string is ever inline. */
  routingKey: string;
  /**
   * snake_case name for the artifact this step produces. Unique across the
   * template. Other steps reference it as a bare string, exactly like a step
   * `key`, so it carries the same character set rather than a second one.
   */
  output: string;
  /** Fan-out width: N concurrent drafts sharing one prompt. Absent means 1. */
  count?: number;
  /**
   * `input.<key>`, where `<key>` is a declared `text` or `select` input holding
   * how many variants this run should write. The fence `count` is the ceiling
   * and the fallback when the input is empty. A `select` option's `value` must
   * be an integer string between 1 and that ceiling.
   *
   * md -> ts: `count_from` -> `countFrom`.
   */
  countFrom?: string;
  /** Skill slug injected as one static block (J3). Absent means no skill. */
  skill?: string;
  /** Upstream step keys whose outputs feed this prompt. Backward references only. */
  inputsFrom?: Array<string>;
  /**
   * `input.<key>`, where `<key>` is a declared `text` or `select` input holding
   * the recency window the search runs over, written the way a person writes
   * one: `30 days`, `6 months`, `since March`, in any language.
   *
   * It is a phrase rather than a number because the number is not the thing the
   * author knows: `services/assistant_time_window.py` turns the phrase into
   * days with one cheap extraction call, and it is the only place in the
   * platform allowed to do so. A field typed as an integer here would push a
   * parser into the compiler, and a parser over human periods is a keyword list
   * that covers English and quietly fails everything else.
   *
   * Only a research step (one with no `inputsFrom`) reads it, so declaring both
   * this and `inputsFrom` is a compile error rather than a field that runs
   * nowhere. Absent means the search runs on its own default window.
   *
   * md -> ts: `window_from` -> `windowFrom`.
   */
  windowFrom?: string;
  /**
   * A registered output-check capability id (C4). Absent means no check, and a
   * step that declares none behaves exactly as it did before this field
   * existed: one completion, one artifact, no repair pass.
   *
   * The id resolves at RUN time, in the backend's output-check registry, to a
   * pure checker that reads the completion and either passes it or hands back
   * structural repair notes. It is a capability id and never a template id, a
   * step kind or a lane name: the brief step must not know which template
   * declared it, which is the whole reason the check is declared here rather
   * than branched on inside the step.
   *
   * md -> ts: `output_check` -> `outputCheck`.
   */
  outputCheck?: string;
  /** Prose body between this step's fence and the next H2. Markdown. */
  instructions: string;
}

/**
 * A human approval stop. Artifacts are shown for review, and when `editable`
 * the edits are submitted as one payload with the approval; executors then read
 * `coalesce(output_override, output)`.
 */
export interface WorkflowGateStepConfig extends WorkflowStepConfigCommon {
  kind: "gate";
  /** Step keys whose artifacts are shown for approval. Non-empty. */
  artifacts: Array<string>;
  editable: boolean;
}

/**
 * A twin test over the existing quick-study engine.
 *
 * `maxQuestions` is hard-validated at compile time, not at run time: this is
 * the step that spends twin credits, so the ceiling belongs in the contract.
 * Platform-wide cap is 2 (D5,
 * `docs/plans/2026-09-14-twin-test-structured-revision-and-gate-collapse-plan.md`):
 * a workflow that needs more questions adds more test steps rather than
 * raising this ceiling. The frontend gate reads this field off the run's own
 * template instead of mirroring the number by hand.
 */
export interface WorkflowQuickStudyStepConfig extends WorkflowStepConfigCommon {
  kind: "quick_study";
  /** Step key producing the questions. */
  questionsFrom: string;
  /** `input.<key>`, where `<key>` is a declared input of type `audience`. */
  audienceFrom: string;
  /** The platform-wide cap (2). Outside that range is a compile error. */
  maxQuestions: number;
  /**
   * Twins drawn for the panel. Positive integer, and optional: a template may
   * leave it unset to take the step's own default, which is what lets a size
   * agreed earlier in the same thread (D15,
   * `docs/plans/twin-test-qs-reuse.md`) carry forward instead of a template
   * literal overriding it every run. When present it is always honored as the
   * explicit, highest-priority source.
   */
  panelSize?: number;
  /**
   * Whether each question also asks its linked "why" open end. Optional and
   * defaults to `true` when absent, so a template written before this field
   * existed keeps running with the open end on and needs no edit.
   *
   * md -> ts: `paired_open_end` -> `pairedOpenEnd`.
   */
  pairedOpenEnd?: boolean;
}

/** An outbound Slack post through the org webhook. */
export interface WorkflowSlackWebhookStepConfig extends WorkflowStepConfigCommon {
  kind: "slack_webhook";
  /** Step key whose output is delivered. */
  contentFrom: string;
}

/**
 * Assembles named upstream outputs into one written document.
 *
 * A report is LLM-written prose, not a concatenation, so it routes like every
 * other model call: `routingKey` is required and no executor ever names a model
 * itself.
 *
 * It is also AUTHORED prose, so it carries the same two authoring affordances a
 * `prompt` does: an optional `skill` and an optional instruction body. Both are
 * optional rather than required because the report charter is a complete
 * instruction set on its own, and the flagship template writes neither.
 *
 * md -> ts: `routing_key` -> `routingKey`, `sections_from` -> `sectionsFrom`,
 * `skill` -> `skill`.
 */
export interface WorkflowReportStepConfig extends WorkflowStepConfigCommon {
  kind: "report";
  /** Must resolve in `config/llm-routing.yaml`. No model string is ever inline. */
  routingKey: string;
  /** Step keys, in render order. Non-empty. */
  sectionsFrom: Array<string>;
  /**
   * Skill slug injected as one static block (J3). Absent means
   * `WORKFLOW_REPORT_SKILL_SLUG`, not "no skill": a report always has house
   * guidance available, and the field only chooses a different one.
   */
  skill?: string;
  /**
   * Prose body between this step's fence and the next H2. Markdown. Absent when
   * the author wrote none, which is the common case.
   */
  instructions?: string;
}

/**
 * Default skill for a `report` step whose fence names none.
 *
 * `WorkflowReportStepConfig.skill` is optional: absent means this slug, not
 * "no skill". One definition so the executor and the capability chips name
 * the same house guidance.
 */
export const WORKFLOW_REPORT_SKILL_SLUG = "report-writing";

export type WorkflowStepConfig =
  | WorkflowPromptStepConfig
  | WorkflowGateStepConfig
  | WorkflowQuickStudyStepConfig
  | WorkflowSlackWebhookStepConfig
  | WorkflowReportStepConfig;

/**
 * The compiled form of a template markdown file. Stored verbatim in
 * `workflow_templates.compiled` (jsonb) alongside the `md_body` it came from
 * and the `md_hash` that pins the pair.
 *
 * This is the ONLY home for the human name and the description: the
 * `workflow_templates` table has no `name` or `description` column, so the API
 * projections below read them out of here. `name` and `version` are duplicated
 * into columns (`slug`, `version`) purely so the unique index can exist; the
 * jsonb copy is what the compiler wrote and the two must agree.
 */
export interface WorkflowCompiledTemplate {
  /** Frontmatter `name`: kebab-case. Mirrors `workflow_templates.slug`. */
  name: string;
  /** Frontmatter `display-name`. Surfaced as `displayName` on API shapes. */
  displayName: string;
  /** Frontmatter `description`. */
  description: string;
  /** Frontmatter `version`. Mirrors `workflow_templates.version` verbatim. */
  version: string;
  /**
   * Template-level prose before the first H2 (`document.preamble`). Always
   * present: an empty preamble compiles to `""`, never a missing key.
   */
  overview: string;
  inputs: Array<WorkflowInputSpec>;
  steps: Array<WorkflowStepConfig>;
}

/* ------------------------------------------------------------------------ */
/* Template records                                                          */
/* ------------------------------------------------------------------------ */

/**
 * Org overlay vs Fairgen seed for one overridable workflow step.
 *
 * Sibling of `WorkflowTemplate.steps[].instructions` (D6). On the DETAIL
 * response, `WorkflowStepConfig.instructions` is the RESOLVED body the run
 * will use (org overlay when one exists, otherwise the seed). This object is
 * the editor's view of the same step: `baseInstructions` is always the
 * Fairgen seed body, and `isCustomized` says whether an overlay row is in
 * force. Same split as `Skill.instructions` beside
 * `Skill.orgOverrideInstructions`. The client must not re-derive which steps
 * are overridable (D5: prompt and report only); `stepCustomizations` is that
 * list.
 *
 * Org-only (D14). No personal layer, no `userId`, no per-user copy. Any org
 * member may edit. Attribution is a display name only.
 *
 * DDL (`workflow_step_overrides`): `step_key` -> `stepKey`, overlay
 * `instructions` resolve into `steps[].instructions` rather than travelling
 * here, `updated_at` -> `updatedAt`, `updated_by_user_id` -> `updatedByName`.
 * Not exposed: row `id`, `token`, `organization_id`, `template_slug`,
 * `updated_by_user_id`.
 *
 * A step that has never been customized: `isCustomized: false`,
 * `updatedAt: null`, `updatedByName: null`.
 */
export interface WorkflowStepCustomization {
  /** snake_case step key, unique within the template. */
  stepKey: string;
  /** Fairgen seed body. Always the compiled seed, never the overlay. */
  baseInstructions: string;
  /** True when an org overlay row exists for this step. */
  isCustomized: boolean;
  /**
   * Optimistic-concurrency token (D22), not a display-only timestamp.
   *
   * The client sends this back as `expectedUpdatedAt` on
   * `UpdateWorkflowStepInstructionsPayload`. The upsert is
   * `WHERE updated_at = expected`; a mismatch is a 409. Null means there is
   * no overlay row yet, so the first write omits `expectedUpdatedAt`. Do not
   * drop this field from the projection: without it the client cannot guard
   * a concurrent save.
   *
   * Typed `string | null` (required key, nullable value) so a missing
   * overlay is representable and an omitted key cannot silently skip the
   * concurrency guard.
   */
  updatedAt: string | null;
  /**
   * Display name of whoever last saved this overlay, for the Step 8
   * "last changed by" line on a customized step.
   *
   * NULLABLE because `updated_by_user_id` is nullable with ON DELETE SET
   * NULL: a shared per-org overlay has to outlive the member who last
   * edited it. Same reason as `WorkflowTemplateSettings.updatedByName`.
   * Never customized: null. Never a user id (D14).
   */
  updatedByName: string | null;
}

/**
 * Body of `PUT /workflows/:token/steps/:stepKey/instructions`.
 *
 * Prose only (D8). One field for the instruction body. The fence
 * (`panelSize`, `count`, `routingKey`, `output`, `maxQuestions`) is not
 * writable here and must never be added. Full-body replacement (D10), never
 * a diff.
 *
 * Org-only (D14): no `userId` on this payload. The acting user is taken from
 * the session. The overlay is keyed `(org, slug, step)`.
 */
export interface UpdateWorkflowStepInstructionsPayload {
  /** Replacement instruction body. Markdown. */
  instructions: string;
  /**
   * Optimistic-concurrency token (D22). The `updatedAt` the client loaded
   * from `WorkflowStepCustomization`. Absent means "first write": there was
   * no overlay row when the editor opened. A stale token is a 409, not a
   * silent overwrite.
   */
  expectedUpdatedAt?: string;
}

/**
 * A workflow template as the API returns it.
 *
 * DDL mapping (`workflow_templates`): `token` -> `token`, `slug` -> `slug`,
 * `version` -> `version`, `md_body` -> `mdBody`, `md_hash` -> `mdHash`,
 * `is_active` -> `isActive`, `organization_id IS NULL` -> `isSeeded` (a null
 * org means Fairgen-seeded, and the org id itself never crosses the wire). Row
 * `id` is not exposed.
 *
 * `compiled` (jsonb) supplies the rest: `compiled.displayName` -> `displayName`,
 * `compiled.description` -> `description`, `compiled.overview` -> `overview`,
 * `compiled.inputs` -> `inputs`, `compiled.steps` -> `steps`. There are no
 * `name` or `description` columns to read instead, so the projection must go
 * through `WorkflowCompiledTemplate`. `overview` is the compiled preamble and
 * is rendered on the workflow detail page; `mdBody` is the template source
 * and is not shown there.
 *
 * `stepCustomizations` is projected from `workflow_step_overrides` (org
 * overlay vs seed), not from `compiled`. Required on this detail shape.
 * `WorkflowTemplateSummary` / `toSummary` do not carry it.
 */
export interface WorkflowTemplate {
  token: string;
  /** Column `slug`, equal to `compiled.name` (frontmatter `name`). */
  slug: string;
  /**
   * Author-declared version, pinned verbatim onto every run. String, not a
   * number: the column is `varchar(50)` and frontmatter may carry `1`, `1.2`
   * or `1.2.0`, all of which must round-trip byte for byte.
   */
  version: string;
  displayName: string;
  description: string;
  /**
   * Compiled template-level prose (`compiled.overview`). Always a string;
   * empty when the markdown had no preamble.
   */
  overview: string;
  /** Raw markdown including frontmatter. The renderer strips frontmatter. */
  mdBody: string;
  /** Hash of `mdBody`, pinned onto every run started from this template. */
  mdHash: string;
  inputs: Array<WorkflowInputSpec>;
  /**
   * Compiled steps. On this DETAIL response, `instructions` is the RESOLVED
   * text (org overlay when present, otherwise the Fairgen seed). The compiler
   * contract on `WorkflowStepConfig` is unchanged (D6). Base vs overlay lives
   * on `stepCustomizations`.
   */
  steps: Array<WorkflowStepConfig>;
  /**
   * One entry per overridable step (`prompt` and `report`), not only the
   * overridden ones. Required, never optional. An empty array is the honest
   * "nothing customized" only when the template has zero overridable steps.
   * Typically this lists every prompt and report so an editor can read the
   * base text of an untouched step without re-deriving D5's predicate.
   *
   * Absent from `WorkflowTemplateSummary` (`toSummary` stays untouched).
   */
  stepCustomizations: Array<WorkflowStepCustomization>;
  /** True when `organization_id IS NULL`: a Fairgen-seeded template. */
  isSeeded: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * List-row projection. No `mdBody`, no compiled steps.
 *
 * `slug`, `displayName` and `description` all originate in `compiled`
 * (`compiled.name`, `compiled.displayName`, `compiled.description`); only
 * `slug` and `version` also exist as columns.
 */
export interface WorkflowTemplateSummary {
  token: string;
  slug: string;
  version: string;
  displayName: string;
  description: string;
  stepCount: number;
  isSeeded: boolean;
  isActive: boolean;
  /**
   * Grouping metadata only (D5). `'thread'` = `thread-action-*` command
   * templates; `'lobby'` = everything else. Older servers omit this — treat
   * `undefined` as `'lobby'`. Never use `surface` as the list-hide filter;
   * `includeThreadActions` stays the server default.
   */
  surface?: "lobby" | "thread";
  /**
   * `workflow_templates.created_at`. For a seeded template this is when the
   * seeder first inserted it in THIS environment, not when anyone authored it:
   * the registry has no author column and no authored-on date, so the list's
   * Created column is honest about row age and nothing more.
   */
  createdAt: string;
  updatedAt: string;
}

/* ------------------------------------------------------------------------ */
/* Run status vocabulary                                                     */
/* ------------------------------------------------------------------------ */

/**
 * `workflow_runs.status`. These are DB values, and this array is byte-identical
 * to the `workflow_run_status` pg enum in
 * `apps/backend/src/db/schema/workflows.ts`. UI copy such as "Waiting on you"
 * is a render-time label over `awaiting_approval` and is never stored.
 *
 * Changing this list means changing the enum, which means a migration, which
 * only Step 0.3 may run.
 */
export const WORKFLOW_RUN_STATUSES = [
  "queued",
  "running",
  "waiting_external",
  "awaiting_approval",
  "paused",
  "completed",
  "failed",
  "cancelled",
] as const;

export type WorkflowRunStatus = (typeof WORKFLOW_RUN_STATUSES)[number];

/**
 * Terminal run statuses. These are exactly the statuses excluded from the
 * one-active-run-per-thread partial unique index, so the two must not drift.
 */
export const WORKFLOW_RUN_TERMINAL_STATUSES: ReadonlyArray<WorkflowRunStatus> =
  ["completed", "failed", "cancelled"];

export function isWorkflowRunTerminal(status: WorkflowRunStatus): boolean {
  return (
    WORKFLOW_RUN_TERMINAL_STATUSES as ReadonlyArray<string>
  ).includes(status);
}

/** Run statuses that hold the surface open. The complement of terminal. */
export const WORKFLOW_RUN_ACTIVE_STATUSES: ReadonlyArray<WorkflowRunStatus> = [
  "queued",
  "running",
  "waiting_external",
  "awaiting_approval",
  "paused",
];

/** `workflow_runs.trigger`, byte-identical to the `workflow_run_trigger` enum. */
export const WORKFLOW_RUN_TRIGGERS = [
  "user",
  "schedule",
  "thread_action",
] as const;

export type WorkflowRunTrigger = (typeof WORKFLOW_RUN_TRIGGERS)[number];

/**
 * `workflow_run_steps.state`. The run vocabulary minus `paused` (a pause is a
 * run-level fact) plus `skipped` (a step the engine passed over, for example a
 * gate on an auto-approve run).
 *
 * The column is varchar rather than a pg enum on purpose, so that Step 1.2 can
 * widen the vocabulary without a migration in a tree where only Step 0.3 may
 * run `db:generate`. That makes this array the only enforcement there is.
 */
export const WORKFLOW_RUN_STEP_STATES = [
  "queued",
  "running",
  "waiting_external",
  "awaiting_approval",
  "completed",
  "failed",
  "skipped",
  "cancelled",
] as const;

export type WorkflowRunStepState = (typeof WORKFLOW_RUN_STEP_STATES)[number];

export const WORKFLOW_RUN_STEP_TERMINAL_STATES: ReadonlyArray<WorkflowRunStepState> =
  ["completed", "failed", "skipped", "cancelled"];

export function isWorkflowRunStepTerminal(
  state: WorkflowRunStepState,
): boolean {
  return (
    WORKFLOW_RUN_STEP_TERMINAL_STATES as ReadonlyArray<string>
  ).includes(state);
}

export function isWorkflowRunStepState(
  value: string,
): value is WorkflowRunStepState {
  return (WORKFLOW_RUN_STEP_STATES as ReadonlyArray<string>).includes(value);
}

/**
 * `workflow_run_steps.gate_decision`, and the body of `POST .../gate`.
 * varchar-backed, so this array is the only enforcement.
 */
export const WORKFLOW_GATE_DECISIONS = ["approve", "reject"] as const;

export type WorkflowGateDecision = (typeof WORKFLOW_GATE_DECISIONS)[number];

export function isWorkflowGateDecision(
  value: string,
): value is WorkflowGateDecision {
  return (WORKFLOW_GATE_DECISIONS as ReadonlyArray<string>).includes(value);
}

/* ------------------------------------------------------------------------ */
/* Run step payloads                                                         */
/* ------------------------------------------------------------------------ */

/** One renderable unit of a step's output. Fanned-out prompts emit N of these. */
export interface WorkflowArtifact {
  /** The producing step's `output` slug. */
  slug: string;
  title: string;
  /** Markdown. Rendered through the shared `MarkdownBody`. */
  body: string;
  /** 0-based position when the producing step declared `count` > 1. */
  index?: number;
}

export interface WorkflowArtifactOutput {
  kind: "artifacts";
  artifacts: Array<WorkflowArtifact>;
}

/**
 * One question's quick-study result, exactly as python wrote it.
 *
 * Deliberately NOT enumerated, and that is the contract rather than laziness.
 * `QuickStudyResult` carries ~60 fields and keeps growing: the resolved plot id
 * and its allowed alternates, every response row, the metric headline, the
 * verdict and its separability notes, the prose narrative, the open-end theme
 * clusters and word frequencies, the grounding gates, and for a monadic run a
 * full set of per-item aggregates. Every attempt to list "the interesting
 * subset" has dropped something a renderer gates on, so the finding carries the
 * object verbatim: it cannot drift when python adds a field, and each consumer
 * narrows it at the point of use against its own view of a quick-study result.
 *
 * Response rows travel inline and uncapped. The charts aggregate client-side
 * from `(question, responses)`, so a reference or a cap would leave the
 * renderer either with no rows or with silently distorted percentages. Size is
 * bounded by the panel-size clamp instead.
 *
 * ONE narrowing, and it is a real amendment rather than an oversight: the
 * producer projects `responses[].attributes` down to the keys a renderer can
 * actually address (the clean-segment family, the identity roots, the fixed
 * name/demographic set, the B2B firmographic matches). The engine echoes back
 * all 43-51 raw imported columns of the source study, which measured at 96-98%
 * of every row: 5,737 bytes a row against 1,024 projected, and 8.21MB against
 * 1.47MB for the worst run this lane can produce. Nothing addressable is lost,
 * `segment_labels` is untouched, and the durable `result.json` in GCS still
 * holds every column. The projection is deliberately OUTBOUND only - see
 * `wholeResult` in `twin-test-findings.ts` for why trimming the same columns on
 * the way IN would risk making the twins themselves dumber.
 */
export interface WorkflowQuickStudyResultPayload {
  [key: string]: unknown;
}

/**
 * What kind of measure a quick-study finding actually carries. `scored`: the
 * question's registry `summaryMeasure` is `scored`, so `topAnswerLabel` /
 * `topAnswerPercent` are populated from a real tally. `themes`: an open-end
 * question; the summary lives in `result.themes`, not in a top answer.
 * `per_version`: a monadic finding (one exposure per version); there is no
 * pooled summary to report. `unmeasured`: the question type has no
 * single-number summary at all (the engine may still score per option
 * elsewhere; this finding does not).
 */
export type WorkflowQuickStudyMeasureKind = "scored" | "themes" | "per_version" | "unmeasured";

export interface WorkflowQuickStudyFinding {
  question: string;
  headline: string;
  /** Populated only when `measureKind` is `scored`; `''` otherwise. */
  topAnswerLabel: string;
  /** 0..100. Populated only when `measureKind` is `scored`; `0` otherwise. */
  topAnswerPercent: number;
  /** One representative open-end, from the `paired_oe` half of the question. */
  verbatim: string;
  /**
   * Optional for compatibility only: findings persisted before this field
   * existed carry none. A reader treats an absent value as legacy: render
   * themes when `result.themes` is non-empty, skip the top answer when the
   * label is empty, otherwise the legacy top-answer line.
   */
  measureKind?: WorkflowQuickStudyMeasureKind;
  /**
   * The whole result behind the five summary fields above.
   *
   * Optional for compatibility only: findings persisted before this field
   * existed, and fixtures that exercise the summary alone, carry none. Every
   * finding produced today carries it, so a consumer that needs insights or
   * plots reads this and treats an absent value as "summary only".
   */
  result?: WorkflowQuickStudyResultPayload;
}

export interface WorkflowQuickStudyOutput {
  kind: "quick_study";
  /** `workflow_run_steps.external_ref`: the quick-study run token. */
  quickStudyRunToken: string;
  panelSize: number;
  questionCount: number;
  findings: Array<WorkflowQuickStudyFinding>;
  /**
   * Display name of the audience the panel was drawn from. Optional: older
   * findings and summary-only fixtures omit it. The report writer uses it on
   * quote attribution (`Audience, n=N`) rather than inventing a label.
   */
  audienceName?: string | null;
  /**
   * Present when the drawn panel is smaller than the requested size. The run
   * still executes on the matching twins; this is the user-visible note, not a
   * refusal.
   */
  shortPanelNote?: string;
}

export interface WorkflowDeliveryOutput {
  kind: "delivery";
  channel: "slack";
  deliveredAt: string;
  /** `workflow_run_steps.external_ref`: the Slack message ts. */
  messageRef: string;
  charCount: number;
}

export interface WorkflowGateOutput {
  kind: "gate";
  decision: WorkflowGateDecision;
  editedArtifactCount: number;
  note: string | null;
}

export type WorkflowStepOutput =
  | WorkflowArtifactOutput
  | WorkflowQuickStudyOutput
  | WorkflowDeliveryOutput
  | WorkflowGateOutput;

export interface WorkflowStepError {
  code: string;
  message: string;
  occurredAt: string;
  /** False once attempts are exhausted or the failure is not retryable. */
  retryable: boolean;
}

/* ------------------------------------------------------------------------ */
/* Run records                                                               */
/* ------------------------------------------------------------------------ */

/**
 * One step of a run.
 *
 * DDL mapping (`workflow_run_steps`): `id` (uuid PK) -> `stepId`, `step_index`,
 * `step_key`, `step_kind`, `state`, `attempt`, `max_attempts`,
 * `input` -> `input`, `output` -> `output`, `output_override` -> `outputOverride`,
 * `external_ref`, `approved_at`, `gate_decision`, `gate_expires_at`, `error`,
 * `created_at`/`updated_at`/`started_at`/`completed_at`.
 *
 * Not exposed: `run_id` (the parent carries the token), `job_id` (a BullMQ
 * internal), and `approved_by` (a user row id, surfaced as `approvedByName`).
 *
 * This table is the one exception to the `token` convention: its primary key is
 * already a uuid, so it has no separate `token` column and none is invented
 * here. `stepId` is that uuid, which is opaque and safe to expose; the no-row-id
 * rule is about serials.
 */
/** Active quick-study sub-run counts, joined onto a sampling `quick_study` step. */
export interface WorkflowRunStepProgress {
  completed: number | null;
  total: number | null;
  /**
   * 0-based index of the in-flight twin-test question. Null/absent when the
   * join has no active sub-run yet. Additive: older clients ignore it.
   */
  questionIndex?: number | null;
}

export interface WorkflowRunStep {
  /**
   * The row's uuid primary key. Travels outward as `cost_events.sourceId` and
   * inside BullMQ job ids, which is why the PK is opaque in the first place.
   */
  stepId: string;
  stepIndex: number;
  stepKey: string;
  stepKind: WorkflowStepKind;
  /** The template step's H2 heading, denormalized so a list row renders alone. */
  title: string;
  state: WorkflowRunStepState;
  attempt: number;
  maxAttempts: number;
  input: WorkflowJsonObject | null;
  output: WorkflowStepOutput | null;
  /** Gate edits. Executors read `outputOverride ?? output`, never `output` alone. */
  outputOverride: WorkflowStepOutput | null;
  /** Quick-study run token or Slack ts. Written before the external call. */
  externalRef: string | null;
  /** Display name of the approver. The `approved_by` row id stays server-side. */
  approvedByName: string | null;
  approvedAt: string | null;
  gateDecision: WorkflowGateDecision | null;
  /** Approval deadline (approx. gate creation + 7 days). Sweeper fails past it. */
  gateExpiresAt: string | null;
  error: WorkflowStepError | null;
  /** Server-derived: this step is `failed` and a retry is still allowed. */
  isRetryable: boolean;
  /**
   * Persisted QS `{completed, total}` for a `quick_study` step that is
   * `running` or `waiting_external`. Null when there is no active sub-run yet,
   * the step is terminal, or the kind is not `quick_study`. Additive.
   */
  progress?: WorkflowRunStepProgress | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One captured turn of the thread conversation a run was started from. */
export interface WorkflowConversationMessage {
  role: "user" | "assistant";
  text: string;
  createdAt: string;
}

/** `workflow_runs.conversation_snapshot`. */
export interface WorkflowConversationSnapshot {
  threadToken: string;
  capturedAt: string;
  messages: Array<WorkflowConversationMessage>;
  /** True when the snapshot hit the declared token budget and dropped oldest-first. */
  truncated: boolean;
}

/**
 * A workflow run as the API returns it.
 *
 * DDL mapping (`workflow_runs`): `token`, `status`, `trigger`,
 * `current_step_index`, `inputs`, `conversation_snapshot`, `auto_approve`,
 * `idempotency_key`, `cancel_requested_at`, `error`, `template_version` ->
 * `templateVersion`, `template_md_hash` -> `templateMdHash`, `thread_id` ->
 * `threadToken`, `template_id` -> `templateToken` + `templateSlug` +
 * `templateDisplayName`, plus timestamps.
 *
 * Not exposed: row `id`, `organization_id`, `user_id`, `template_id`. Org and
 * user are enforced server-side; the only thing the client needs from `user_id`
 * is `viewerOwnsRun`, since v1 lets only the run's owner act on a gate.
 *
 * `resolvedInsightScope` is pinned at createRun from InsightScope resolution
 * (org default plus the run actor's personal layer). It is not a live read of
 * the viewer's current scope.
 */
export interface WorkflowRun {
  token: string;
  status: WorkflowRunStatus;
  trigger: WorkflowRunTrigger;
  /** Null for runs started outside a thread. */
  threadToken: string | null;
  templateToken: string;
  templateSlug: string;
  templateDisplayName: string;
  /**
   * Pinned verbatim at creation from `workflow_templates.version`. A later
   * template edit never moves a live run. String, not a number: the column is
   * `varchar(50)` and `1`, `1.2` and `1.2.0` are all valid and all distinct.
   */
  templateVersion: string;
  /** Pinned at creation, alongside `templateVersion`. */
  templateMdHash: string;
  /**
   * InsightScope this run resolved, pinned at createRun (D13).
   *
   * Two people running the same workflow can get different personal layers,
   * so the run detail must show what THIS run resolved rather than the
   * viewer's current scope. Null when the org has no InsightScope row
   * (contributes zero bytes to the prompt). Required on the type. Do not
   * make it optional and treat absent as "not projected."
   */
  resolvedInsightScope: InsightScope | null;
  currentStepIndex: number;
  inputs: WorkflowInputValues;
  conversationSnapshot: WorkflowConversationSnapshot | null;
  /**
   * Living thread artifacts snapshotted at createRun (sibling of
   * `conversationSnapshot`). Empty outside a thread or when none exist.
   * Briefing seam — not `commandSeed`. Optional on older clients; Nest always
   * projects an array (possibly empty).
   */
  livingArtifacts?: Array<ThreadArtifactDto>;
  autoApprove: boolean;
  idempotencyKey: string | null;
  cancelRequestedAt: string | null;
  error: WorkflowStepError | null;
  /** Full step list, ordered by `stepIndex`. Replayed whole on socket subscribe. */
  steps: Array<WorkflowRunStep>;
  /** Server-derived from `user_id`: only the owner may act on a gate in v1. */
  viewerOwnsRun: boolean;
  /** Display name of the run's owning user. */
  ownerName: string;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** List-row projection for a runs list. */
export interface WorkflowRunSummary {
  token: string;
  status: WorkflowRunStatus;
  trigger: WorkflowRunTrigger;
  templateSlug: string;
  templateDisplayName: string;
  threadToken: string | null;
  currentStepIndex: number;
  stepCount: number;
  updatedAt: string;
}

/**
 * Body of `POST /workflow-runs` (`CreateWorkflowRunDto`).
 *
 * Canonical copy of the create-run write shape. Field names and comments
 * match the existing frontend payload and DTO. `note` is optional (Step 9:
 * today's note) and is not required on older clients.
 */
export interface CreateWorkflowRunPayload {
  /** `workflow_templates.token`. Row ids never cross the wire. */
  templateToken: string;
  /** Thread the run reports into. Absent for a run started outside one. */
  threadToken?: string;
  /** Values keyed by `WorkflowInputSpec.key`. */
  inputs?: WorkflowInputValues;
  autoApprove?: boolean;
  trigger?: WorkflowRunTrigger;
  /**
   * Caller-supplied dedupe key. A repeat POST with the same key returns the run
   * that already exists rather than starting a second one.
   */
  idempotencyKey?: string;
  /**
   * Structured twin-test segment. Sibling of `inputs`, never a `rest` string.
   * Absent = today's full-audience draw.
   */
  populationFilter?: PopulationFilter;
  segmentLabel?: string;
  /**
   * Today's note (Step 9). This run, this person. Typed at Start run and
   * never saved as a setting. Appended to `conversationSnapshot` as a user
   * turn. Absent means no note. Scheduled runs carry none.
   */
  note?: string;
  /**
   * Typed sentence that started this run. Persisted as the user turn and
   * appended to the briefing snapshot. Omitted when empty/whitespace.
   * Intake-settled starts do not send this. Max 4000 on the server.
   */
  askTurn?: string;
  /**
   * Answer language for a thread the server mints when `threadToken` is
   * absent. Ignored when a thread is already in scope, since that thread
   * carries its own. Absent leaves the minted thread unpicked, which is the
   * honest answer for a scheduled fire.
   */
  language?: string;
}

/* ------------------------------------------------------------------------ */
/* Schedules                                                                 */
/* ------------------------------------------------------------------------ */

/**
 * `workflow_schedules.cadence`. v1 fires on the UTC hour, and the UI says so.
 * varchar-backed, so this array is the only enforcement.
 */
export const WORKFLOW_SCHEDULE_CADENCES = [
  "daily",
  "weekdays",
  "weekly",
] as const;

export type WorkflowScheduleCadence =
  (typeof WORKFLOW_SCHEDULE_CADENCES)[number];

export function isWorkflowScheduleCadence(
  value: string,
): value is WorkflowScheduleCadence {
  return (WORKFLOW_SCHEDULE_CADENCES as ReadonlyArray<string>).includes(value);
}

/**
 * DDL mapping (`workflow_schedules`): `token`, `inputs`, `cadence`,
 * `hour_utc` -> `hourUtc`, `enabled`, `auto_approve` -> `autoApprove`,
 * `last_fired_on` -> `lastFiredOn`, `template_id` -> `templateToken` +
 * `templateSlug` + `templateDisplayName`, `created_by_user_id` ->
 * `createdByName` + `viewerOwnsSchedule`.
 *
 * Not exposed: row `id`, `organization_id`, `template_id`, `created_by_user_id`.
 */
export interface WorkflowSchedule {
  token: string;
  templateToken: string;
  templateSlug: string;
  templateDisplayName: string;
  inputs: WorkflowInputValues;
  cadence: WorkflowScheduleCadence;
  /** 0..23, UTC. v1 has no org timezone. */
  hourUtc: number;
  enabled: boolean;
  autoApprove: boolean;
  /** `YYYY-MM-DD` of the last fire, or null. Backs the daily idempotency key. */
  lastFiredOn: string | null;
  /** Display name of the identity a scheduled run executes as. */
  createdByName: string;
  viewerOwnsSchedule: boolean;
  createdAt: string;
  updatedAt: string;
}

/* ------------------------------------------------------------------------ */
/* Template settings                                                         */
/* ------------------------------------------------------------------------ */

/**
 * The org's saved run inputs for one workflow template.
 *
 * Keyed on the template's SLUG, never its id or token, and that is the whole
 * reason this is a separate record rather than a column on the template.
 * `upsertSeededTemplate` targets the unique index on `(slug, version)`, so a
 * version bump INSERTS a new template row with a new id and a new token and
 * `deactivateOtherSeededVersions` retires the old one. Settings keyed on
 * either would be orphaned by every reseed, silently, and the user would find
 * their saved inputs empty with nothing to explain it. The slug is stable
 * across versions.
 *
 * DDL mapping (`workflow_template_settings`): `token`, `inputs`,
 * `updated_by_user_id` -> `updatedByName`, `updated_at` -> `updatedAt`.
 *
 * Not exposed: row `id`, `organization_id`, `slug`, `updated_by_user_id`.
 *
 * `inputs` may be PARTIAL. Required inputs are enforced at `Start run` and at
 * schedule fire time, never at save: saving a config is not running it.
 *
 * **`GET` returns `null` when the org has never saved one**, rather than a
 * blank record. A record therefore only ever exists because somebody saved it,
 * which is what keeps `updatedAt` honest: a sentinel row would need an invented
 * timestamp and an empty editor, and the screen would have to tell the two
 * apart anyway to know whether to show "last edited by".
 */
export interface WorkflowTemplateSettings {
  token: string;
  inputs: WorkflowInputValues;
  /**
   * Display name of whoever last saved this, for the "last edited by" line.
   *
   * NULLABLE, because `updated_by_user_id` is nullable with `ON DELETE SET
   * NULL`: a shared per-org config has to outlive the member who last edited
   * it. A `NOT NULL` cascade would delete the org's saved inputs along with
   * that person's account. So attribution degrades and the config survives,
   * and the UI drops the "last edited by" clause rather than inventing a name.
   */
  updatedByName: string | null;
  updatedAt: string;
}

/* ------------------------------------------------------------------------ */
/* Skills                                                                    */
/* ------------------------------------------------------------------------ */

/**
 * A Fairgen seed skill with optional org/personal instruction overlays.
 *
 * Injected into prompt steps and assistant turns as one static prefix segment
 * (J3). Public handle is a derived uuid token (not a DB row id). Name, slug,
 * and description stay seed-owned; only `instructions` may be overridden when
 * skills customization is enabled.
 *
 * `orgOverrideInstructions` / `personalOverrideInstructions` are optional for
 * older clients. Settings `getSkill` populates them when the caller user id is
 * known: `null` means no overlay row (inherit).
 */
export interface Skill {
  token: string;
  slug: string;
  name: string;
  /** Seed description; nullable when the seed omits it. */
  description: string | null;
  /** Effective body after personal → org → seed merge (or seed when flag off). */
  instructions: string;
  /**
   * Factory seed body. Settings Original tab. Optional for older clients;
   * `getSkill` sets it when overlay fields are included.
   */
  seedInstructions?: string;
  /** Version of the seed this skill was created from. */
  seededVersion: string | null;
  /** True when effective normalized body differs from the seed. */
  isCustomized: boolean;
  isActive: boolean;
  /**
   * Seed frontmatter `visibility`. List stays complete; Settings filters
   * `editable` client-side. `getSkill` 404s `internal`.
   */
  visibility: SkillVisibility;
  createdAt: string;
  updatedAt: string;
  /** Org overlay body, or null when inheriting seed. */
  orgOverrideInstructions?: string | null;
  /** Personal overlay body, or null when inheriting org/seed. */
  personalOverrideInstructions?: string | null;
}

/** Seed frontmatter `visibility`. Required on the wire after the gold-standard cut. */
export type SkillVisibility = "editable" | "internal";

/**
 * List-row projection. No `instructions` body.
 * `isCustomized` reflects effective (with userId) vs seed when customization
 * is on. List includes `internal` so workflow chips can resolve labels;
 * Settings filters to `editable` client-side.
 */
export interface SkillSummary {
  token: string;
  slug: string;
  name: string;
  description: string | null;
  isCustomized: boolean;
  isActive: boolean;
  visibility: SkillVisibility;
  updatedAt: string;
}

/* ------------------------------------------------------------------------ */
/* Assistant citations and provenance                                        */
/* ------------------------------------------------------------------------ */

/** Contract version stamped on every assistant answer payload. */
export const ASSISTANT_SCHEMA_VERSION = "v2";

export type AssistantSchemaVersion = "v2";

export type AssistantCitationGranularity =
  | "document"
  | "page"
  | "slide"
  | "sheet_range"
  | "timestamp"
  | "post";

/** A citation into an indexed document in the org's library. */
export interface DocumentCitation {
  documentToken: string;
  documentName: string;
  snippet: string;
  granularity: AssistantCitationGranularity;
  pageNumber?: number | null;
  /** Video chapter time (mm:ss) when `granularity` is `timestamp`. */
  timestampLabel?: string | null;
  /** The cited social post when `granularity` is `post`. */
  post?: SocialCitationPost | null;
  citationToken?: string | null;
}

/** A citation into a page the web-search leg actually read. */
export interface WebCitation {
  url: string;
  title: string;
  publisher?: string;
  /** ISO date or datetime, when the source states one. */
  publishedAt?: string;
}

export type AssistantCitation =
  | ({ kind: "document" } & DocumentCitation)
  | ({ kind: "web" } & WebCitation);

/**
 * Where a span of the answer came from. `judgment` is the honest third value:
 * the model asserted it and no source backs it.
 */
export type AssistantAnswerProvenance = "document" | "web" | "judgment";

/** A provenance-tagged range of the final answer text. */
export interface AssistantAnswerSpan {
  /** Unicode character offsets into the final, trimmed answer text. */
  charStart: number;
  charEnd: number;
  provenance: AssistantAnswerProvenance;
  /** Indexes into the turn's `citations` array. Empty for `judgment`. */
  citationIndexes: Array<number>;
}

export type AssistantEffort = "low" | "medium" | "high";

/**
 * Metering numbers computed for `cost_events` and reused by the composer
 * context ring. `historyTokens` is conversation history only; per-turn
 * retrieval is counted separately because it never persists into history.
 */
export interface AssistantContextUsage {
  historyTokens: number;
  ceiling: number;
  turnRetrievalTokens: number;
}

/** The completed answer for one assistant turn. */
export interface AssistantTurnAnswer {
  schemaVersion: AssistantSchemaVersion;
  answer: string;
  citations: Array<AssistantCitation>;
  spans: Array<AssistantAnswerSpan>;
  effort: AssistantEffort;
  contextUsage: AssistantContextUsage;
  /** True when history hit the ceiling and was truncated oldest-first. */
  truncated: boolean;
  refused?: boolean;
  gaps?: Array<string>;
}

/* ------------------------------------------------------------------------ */
/* Assistant stream vocabulary                                               */
/* ------------------------------------------------------------------------ */

/**
 * ONE vocabulary for assistant turn streaming, replacing the broken wire where
 * python emitted `token` and the frontend read `delta` (so answers arrived in
 * one lump instead of streaming).
 *
 * `tool_start` / `tool_end` are emitted by the v1 deterministic retrieval legs
 * and are reused unchanged by the Phase 4 agentic loop, which is why they are
 * in the vocabulary now rather than added later.
 */
export type AssistantStreamEventType =
  | "delta"
  | "tool_start"
  | "tool_end"
  | "citations"
  | "judgment"
  | "done"
  | "error";

/** Retrieval legs and capability readers a turn may announce. */
/* `analyze_results` is report mode only (D5 of
   docs/plans/2026-09-03-assistant-report-mode.md) and is kept last so the union
   mirrors ASSISTANT_TOOL_SCHEMAS's append order. Members must stay a contiguous
   run of `| "name"`: the python parity test parses this union by regex. */
export type AssistantToolName =
  | "web_search"
  | "document_search"
  | "read_skill"
  | "read_workflow"
  | "read_audience"
  | "read_results"
  | "read_artifact"
  | "update_artifact"
  | "run_workflow"
  | "analyze_audience"
  | "create_artifact"
  | "delete_artifact"
  | "analyze_results"
  | "revise_run_step"
  | "propose_social_tracker";

export type AssistantErrorCode =
  | "retrieval_failed"
  | "model_failed"
  | "timeout"
  | "cancelled"
  | "rate_limited"
  | "internal";

/** Incremental prose. Concatenating every `delta.text` in order is the answer. */
export interface AssistantDeltaEvent {
  type: "delta";
  text: string;
}

/**
 * A retrieval leg started.
 *
 * `label` is ENGLISH-ONLY server copy, and this comment used to claim the
 * opposite ("already localized"). Its only producer is `TOOL_LABELS` in
 * `apps/python-api/api/assistant_turn.py`, a fixed dict of English strings
 * that no language input from the request ever reaches.
 *
 * So a surface rendering in the thread's own language must map `tool` itself and
 * ignore `label`, or it puts an English fragment inside a Portuguese sentence.
 * `ThreadActivityIndicator` does exactly that, through the per-tool `targets`
 * map in `apps/frontend/src/lib/constants/languages.ts`, which is also what lets
 * a tool name this build has never seen degrade to honest generic copy rather
 * than leaking a raw wire name.
 *
 * `label` is still carried rather than dropped: it is the wire's own description
 * of the leg, which is worth having in a log, a trace or an English-only
 * internal view. It is simply not translated copy, and never was.
 */
export interface AssistantToolStartEvent {
  type: "tool_start";
  tool: AssistantToolName;
  label: string;
  /**
   * What this leg is looking at: the model's `query` for search tools, or the
   * `slug` for capability readers. Optional so older producers and calls that
   * omit the arg still parse.
   *
   * This is the model's own string, not localized copy. Surfaces that render
   * in the thread language interpolate it into a translated template.
   */
  query?: string;
}

/** A retrieval leg finished. A failed leg reports `ok: false`, not an `error`. */
export interface AssistantToolEndEvent {
  type: "tool_end";
  tool: AssistantToolName;
  ok: boolean;
  itemCount: number;
}

/** The turn's full citation list. Emitted once, before the first `delta`. */
export interface AssistantCitationsEvent {
  type: "citations";
  items: Array<AssistantCitation>;
}

/** Marks a span the model asserted without a source. */
export interface AssistantJudgmentEvent {
  type: "judgment";
  note: string;
}

/**
 * Terminal success.
 *
 * The five answer-carrying fields below were added at the Phase 2 transport
 * window after the browser wire was read back off the producer
 * (`knowledge.service.ts`, the `type: 'done'` write in
 * `askAssistantTurnStream`). That write emits `askId`, `text`, `citations`,
 * `refused` and `gaps` alongside `truncated`, `contextUsage` and
 * `costEventId`, so a client typed strictly against the original five-field
 * shape could not read the answer at all.
 *
 * They are declared OPTIONAL rather than required on purpose: this type is
 * also the compile-time shape of `ASSISTANT_STREAM_EVENT_TYPES`' fixture and
 * of any producer that emits a bare terminal frame, and making them required
 * would break every existing construction site. A reader that needs a
 * guaranteed answer should normalize (`text ?? ''`, `citations ?? []`) at the
 * transport boundary, which is what `assistantTurnStream` in
 * `apps/frontend/src/lib/api/client.ts` does.
 *
 * Stripped by Nest and NOT on the browser wire, so deliberately absent here:
 * `effort`, `usage`, `judgments`, `retrievalCount`, `staticPrefixTokens`,
 * `schema_version`.
 */
/**
 * Optional Start-prefill bag on a Suggested-plan step.
 * Tokens only — never a DB row id. Frontend drops audience when not ready.
 */
export type PopulationFilterLogic = "AND" | "OR";

export interface PopulationFilterCondition {
  field: string;
  operator?:
    | "equals"
    | "not_equals"
    | "contains"
    | "not_contains"
    | "in"
    | "not_in"
    | "greater_than"
    | "less_than"
    | "is_empty"
    | "is_not_empty";
  value?: string | number | boolean | Array<string>;
  groupType?: "binary" | "multiselect";
  members?: Array<string>;
  options?: Array<string>;
  memberValues?: { [key: string]: Array<string> };
}

/**
 * Structured audience segment. Travels as its own field on prepare / commandSeed
 * / start-run — never serialized into `rest` prose.
 */
export interface PopulationFilter {
  logic?: PopulationFilterLogic;
  conditions?: Array<PopulationFilterCondition | PopulationFilter>;
}

export interface AssistantPlanStepInputs {
  audience?: string;
}

/**
 * One registry-enriched plan step on the assistant done / message wire.
 *
 * Nest projects `displayName` + `flags` from the capability registry. Optional
 * `title` (verb card label) and `inputs.audience` (confirm prefill) pass
 * through from python's `parse_plan_tail` when present.
 */
export interface AssistantPlanStep {
  kind: "skill" | "workflow";
  slug: string;
  why?: string;
  title?: string;
  inputs?: AssistantPlanStepInputs;
  displayName: string;
  flags?: Array<
    "spendsTwins" | "postsSlack" | "needsAudience" | "threadAction"
  >;
}

/**
 * Validated plan proposal on an ordinary assistant turn's `done` frame and
 * the proposing assistant message. `cardEligible` is true only for ≥ 2 steps.
 */
export interface AssistantPlanTail {
  steps: Array<AssistantPlanStep>;
  cardEligible: boolean;
}

/**
 * One unconfirmed creative in a message-scoped draft set.
 *
 * Python's `[[CREATIVE_DRAFT]]` tail carries `{ letter, title, body, kind }`.
 * Nest assigns `key` on persist and stamps `confirmedAt` / `publicId` after
 * the client confirm route writes living rows. `updatedAt` is the draft
 * entry's last autosave, used to refuse a stale confirm.
 *
 * A draft is not a living artifact. Confirm is the only writer of rows.
 */
export interface AssistantCreativeDraft {
  key: string;
  letter: string;
  title: string;
  body: string;
  kind: string;
  updatedAt?: string;
  confirmedAt?: string | null;
  publicId?: string | null;
}

/** Confirm-ready workflow preparation returned by the assistant act tool. */
export interface AssistantPreparedRun {
  status: "prepared";
  commandSeed: {
    slug: string;
    rest: string;
    nonce: number;
    forceConfirm: true;
    audience?: string;
    /** Structured segment. Absent = today's full-audience draw. */
    populationFilter?: PopulationFilter;
    /** Confirm-card label for the segment. Never a substitute for the filter. */
    segmentLabel?: string;
  };
}

/**
 * The frozen report grounding-gate outcome (D7). `pass` = nothing needed
 * trimming; `regen_pass` = one correction pass ran and the result passed;
 * `trimmed` = violating sentences were dropped deterministically and shipped
 * as-is. An empty result after trimming never reaches this shape: python
 * emits an `error` frame instead, with no `report` tail at all.
 */
export type AssistantReportGateStatus = "pass" | "regen_pass" | "trimmed";

/**
 * Report-mode's `done` frame tail (D1/D3). Present only when this turn's
 * resolved mode is `report` (the native `/report` command, or a plan step
 * whose skill is `report-writing`). `markdown` is the gated report body Nest
 * persists as a living `report` artifact; `title` is its digit-free H1.
 * `done.text` stays a one-line notice — the markdown never rides `text`.
 */
export interface AssistantReportTail {
  title: string;
  markdown: string;
  gate: {
    status: AssistantReportGateStatus;
    violations: Array<string>;
    removedSentences: number;
  };
  sources: {
    artifactPublicIds: Array<string>;
    toolCalls: Array<{ tool: string; argsSummary: string }>;
  };
}

export interface AssistantDoneEvent {
  type: "done";
  contextUsage: AssistantContextUsage;
  /** True when history was truncated oldest-first to fit the ceiling. */
  truncated: boolean;
  /**
   * Opaque idempotency token for the turn's `cost_events` row, for support
   * lookups. Never the numeric row id.
   */
  costEventId?: string;
  /** The turn's ask id. Equal to `costEventId` on the current producer. */
  askId?: string;
  /** The complete answer text. Equal to every `delta.text` concatenated. */
  text?: string;
  /** The turn's enriched citation list, identical to the `citations` frame's. */
  citations?: Array<AssistantCitation>;
  /** True when the model declined to answer rather than guessing. */
  refused?: boolean;
  /** Named holes the turn could not fill from any source. */
  gaps?: Array<string>;
  /**
   * Suggested plan when the model emitted a validated `[[PLAN]]` block.
   * Absent when python/Nest dropped the tail. Optional on the shared type
   * because producers may emit a bare terminal frame without a plan.
   */
  plan?: AssistantPlanTail;
  /**
   * Unconfirmed creative set when the model emitted a `[[CREATIVE_DRAFT]]`
   * tail. Absent on ordinary turns. Confirm writes living rows from these
   * bytes; the model is not on that path.
   */
  creativeDrafts?: Array<AssistantCreativeDraft>;
  /** Prepared workflow seed; the frontend opens the existing confirmation path. */
  preparedRun?: AssistantPreparedRun;
  /** Tracker the agent suggested; the client confirms it on a card. */
  socialTrackerProposal?: SocialTrackerProposal;
  /**
   * The persisted `knowledge_thread_messages.token` (never the row id, per
   * the no-DB-row-ids rule) for this turn's user message. Absent when the
   * turn stored no user row: an armed intake turn with an empty composer
   * skips the insert (see `askAssistantTurnStream`), and there is no token
   * to invent for it. A client uses this to reconcile a locally-generated
   * turn id with the server-assigned one, never as a guarantee the field is
   * always present.
   */
  userMessageToken?: string;
  /**
   * The persisted `knowledge_thread_messages.token` for this turn's
   * assistant message. Always present when the turn reaches `done` without
   * throwing, since the assistant row is unconditionally inserted before
   * this frame is written.
   */
  assistantMessageToken?: string;
  /**
   * Report-mode tail (D1/D3): present only when this turn's resolved mode is
   * `report`. Absent on every ordinary and intake turn, so the frame stays
   * byte-compatible with every request sent before report mode existed.
   */
  report?: AssistantReportTail;
}

/** Terminal failure. Mutually exclusive with `done`. */
export interface AssistantErrorEvent {
  type: "error";
  code: AssistantErrorCode;
  message: string;
}

export type AssistantStreamEvent =
  | AssistantDeltaEvent
  | AssistantToolStartEvent
  | AssistantToolEndEvent
  | AssistantCitationsEvent
  | AssistantJudgmentEvent
  | AssistantDoneEvent
  | AssistantErrorEvent;

/**
 * Ordering contract for one turn, asserted by the fixture in
 * `apps/frontend/src/lib/workflows/fixtures.ts`:
 *
 * 1. Zero or more `tool_start` / `tool_end` pairs, one pair per retrieval leg,
 *    interleaved freely because legs run concurrently.
 * 2. At most one `citations` event, after every leg has ended and before the
 *    first `delta`. Absent when nothing was retrieved.
 * 3. Zero or more `delta` events, in order.
 * 4. Zero or more `judgment` events, interleaved with `delta`.
 * 5. Exactly one terminal event: `done` or `error`, never both.
 */
export const ASSISTANT_STREAM_EVENT_TYPES: Array<AssistantStreamEventType> = [
  "delta",
  "tool_start",
  "tool_end",
  "citations",
  "judgment",
  "done",
  "error",
];

/* ------------------------------------------------------------------------ */
/* Insight scope (org default + user overlay)                                */
/* ------------------------------------------------------------------------ */

/**
 * Brand-grounded beat for the insights assistant. Same keys at org and user.
 * Empty string = unspecified. No DB ids or user identity on the wire.
 */
export interface InsightScope {
  brand: string;
  product: string;
  category: string;
  region: string;
  notes: string;
}

/** Partial write body for `PUT /insight-scope` and `PUT /insight-scope/me`. */
export interface InsightScopePatch {
  brand?: string;
  product?: string;
  category?: string;
  region?: string;
  notes?: string;
}
